import {
  type AccordionFormkitSection,
  type FormkitNode,
  LayoutType,
  NodeType,
} from "@hightouch/formkit";
import { useFlags } from "launchdarkly-react-client-sdk";
import { type FC, type ReactNode, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { parseHeadings } from "src/formkit/utils";
import {
  collectDescendantComponents,
  getNestedKeysWithDefault,
} from "src/formkit/component-utils";
import {
  Box,
  Button,
  Column,
  FormField,
  RefreshIcon,
  Row,
  SectionHeading,
} from "@hightouch/htui";
import { Markdown } from "src/ui/markdown";
import { AccordionSection } from "src/components/accordion-section";
import {
  Form,
  FormSection,
  Modifier,
  SyncOverride,
  SyncTemplateLock,
} from "src/formkit/components";
import { useFormkitContext } from "src/formkit/components/formkit-context";
import { hasFieldValues } from "src/components/destinations/utils";
import { FKComponent } from ".";

export interface FKNodeProps {
  node: FormkitNode;
  depth: number;
  context: {
    showLocks?: boolean;
    showOverrides?: boolean;
    showClearable?: boolean;
    clearFields?: (nodeKeys: string[]) => void;
    isNodeVisible?: (node: FormkitNode) => boolean;
  } & Record<string, unknown>;
}

export const FKNode: FC<FKNodeProps> = ({
  node,
  depth,
  context,
}: FKNodeProps) => {
  const formNodeChildren = (node.children ?? []).map((node, index) => {
    const reactKey =
      node.type === NodeType.Component ? node.key : `${node.type}-${index}`;
    return (
      <FKNode key={reactKey} context={context} depth={depth + 1} node={node} />
    );
  });

  const componentChildren = useMemo(
    () => collectDescendantComponents(node),
    [node],
  );
  const componentKeys = componentChildren.map(({ key }) => key);
  // Only the clearable-button branch (sync-template editing) reads these values.
  // Gating the subscription stops a keystroke re-rendering every ancestor node.
  const watchedValues = useWatch({
    name: componentKeys,
    disabled: !context.showClearable,
  });
  const hasValues = useMemo(() => {
    if (!context.showClearable || !Array.isArray(watchedValues)) return false;
    if (componentKeys?.length === 0) return false;
    const formValues = componentKeys.reduce(
      (acc, key, index) => {
        acc[key] = watchedValues[index];
        return acc;
      },
      {} as Record<string, any>,
    );
    return hasFieldValues(componentKeys, formValues, context);
  }, [componentKeys, watchedValues, context]);

  if (node.type === NodeType.Layout) {
    if (
      node.layout === LayoutType.Section ||
      node.layout === LayoutType.Accordion
    ) {
      const { heading, subheading } = parseHeadings(node, context);

      // Do not display the section if none of its fields are visible.
      const isSectionVisible = context.isNodeVisible?.(node) ?? true;
      if (!isSectionVisible) return null;

      if (node.parent) {
        return (
          <Box
            p="0 !important"
            border="none !important"
            bg="transparent !important"
          >
            <Box mb={2}>
              <SectionHeading>{heading}</SectionHeading>
              {subheading && <Markdown>{subheading}</Markdown>}
            </Box>

            <Form>{formNodeChildren}</Form>
          </Box>
        );
      }

      if (node.layout === LayoutType.Accordion) {
        return (
          <FKAccordionSection node={node}>
            {formNodeChildren}
          </FKAccordionSection>
        );
      }

      const canShowClearableButton = context.showClearable && hasValues;

      if (node.size === "small") {
        return (
          <FormField
            isOptional={node.optional}
            label={heading && <Markdown disableParagraphs>{heading}</Markdown>}
            description={subheading ? <Markdown>{subheading}</Markdown> : null}
            rightContent={
              componentChildren.length > 0 ? (
                <>
                  {canShowClearableButton && (
                    <Button
                      size="sm"
                      mr={1}
                      variant="tertiary"
                      icon={RefreshIcon}
                      onClick={() => context.clearFields?.(componentKeys)}
                    >
                      Clear selection
                    </Button>
                  )}
                  {context.showLocks && (
                    <SyncTemplateLock nodes={componentChildren} />
                  )}
                  {context.showOverrides && (
                    <SyncOverride childNodeKeys={componentKeys} />
                  )}
                </>
              ) : null
            }
          >
            {formNodeChildren}
          </FormField>
        );
      }

      const isRightContent =
        componentChildren.length > 0 &&
        (context.showLocks || context.showOverrides || canShowClearableButton);

      return (
        <FormSection
          heading={heading}
          subheading={
            subheading ? <Markdown>{subheading}</Markdown> : undefined
          }
          rightContent={
            isRightContent ? (
              <>
                {(context.showLocks || canShowClearableButton) && (
                  <Row alignItems="flex-start">
                    {canShowClearableButton && (
                      <Button
                        mr={1}
                        variant="tertiary"
                        icon={RefreshIcon}
                        onClick={() => context.clearFields?.(componentKeys)}
                      >
                        Clear selection
                      </Button>
                    )}
                    {context.showLocks && (
                      <SyncTemplateLock nodes={componentChildren} />
                    )}
                  </Row>
                )}
                {context.showOverrides && (
                  <SyncOverride childNodeKeys={componentKeys} />
                )}
              </>
            ) : undefined
          }
          isOptional={node.optional}
        >
          <Form>{formNodeChildren}</Form>
        </FormSection>
      );
    }

    if (node.layout === LayoutType.Form) {
      return <>{formNodeChildren}</>;
    }
  }

  if (node.type === NodeType.Component) {
    return (
      <FKComponent node={node} formNodeContext={context}>
        {formNodeChildren}
      </FKComponent>
    );
  }

  if (node.type === NodeType.Modifier) {
    return <Modifier node={node}>{formNodeChildren}</Modifier>;
  }

  return null;
};

type FKAccordionSectionProps = {
  node: AccordionFormkitSection;
  children: ReactNode;
};

/**
 * Accordion sections only mount their child fields once expanded, and the
 * field-level default effect (see FKComponent) only writes a field's `default`
 * to the form on mount. So a collapsed section's fields stay `undefined` in the
 * saved config even though the UI shows their defaults as selected — a mismatch
 * between what the user sees, what gets persisted, and what the backend reads.
 *
 * To keep collapsed sections consistent with their defaults, apply nested
 * defaults for any descendant key that doesn't already have a value, on mount
 * and again whenever form state changes, since a Show modifier inside the
 * section (e.g. CSV options behind `fileType`) can reveal fields after mount.
 * Keys hidden behind an unsatisfied Show modifier are skipped by
 * getNestedKeysWithDefault, and existing values are never overwritten.
 */
const FKAccordionSection: FC<Readonly<FKAccordionSectionProps>> = ({
  node,
  children,
}) => {
  const { watch, getValues, setValue } = useFormContext();
  const baseContext = useFormkitContext();
  const featureFlags = useFlags();

  useEffect(() => {
    const context = { ...baseContext, featureFlags };
    const applyMissingDefaults = () => {
      const defaults = getNestedKeysWithDefault(node, context, watch);
      for (const [key, value] of Object.entries(defaults)) {
        if (value !== undefined && getValues(key) === undefined) {
          setValue(key, value);
        }
      }
    };
    applyMissingDefaults();
    const subscription = watch(applyMissingDefaults);
    return () => subscription.unsubscribe();
  }, [baseContext, featureFlags, node, watch, getValues, setValue]);

  return (
    <AccordionSection label={node.heading ?? ""}>
      {node.subheading && (
        <Box mt={1}>
          <Markdown>{node.subheading}</Markdown>
        </Box>
      )}
      <Column gap={6}>{children}</Column>
    </AccordionSection>
  );
};
